/**
 * Загрузка и валидация content.json. Singleton-кэш.
 */

import type { ContentJson, ContentPack, ContentPuzzle } from '../data/contentTypes';

function getContentUrl(): string {
  const bucketBase =
    String(import.meta.env.VITE_PUZZLE_BUCKET_BASE_URL ?? 'https://storage.yandexcloud.net/puzzle-game').replace(
      /\/+$/,
      ''
    );
  const explicit = String(import.meta.env.VITE_CONTENT_URL ?? '').trim();
  return explicit || `${bucketBase}/content.json`;
}

let cached: ContentJson | null = null;

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function isContentPuzzle(x: unknown): x is ContentPuzzle {
  if (!isRecord(x)) return false;
  return (
    typeof x.id === 'string' &&
    typeof x.imageKey === 'string' &&
    typeof x.full === 'string' &&
    typeof x.title === 'string' &&
    (x.thumb === undefined || typeof x.thumb === 'string')
  );
}

function isContentPack(x: unknown): x is ContentPack {
  if (!isRecord(x)) return false;
  if (typeof x.id !== 'string' || typeof x.title !== 'string') return false;
  if (!Array.isArray(x.puzzles) || x.puzzles.length < 1) return false;
  return x.puzzles.every(isContentPuzzle);
}

/**
 * Валидирует сырой объект как ContentJson. Выбрасывает Error с понятным сообщением.
 */
export function validateContent(raw: unknown): asserts raw is ContentJson {
  if (!isRecord(raw)) {
    throw new Error('Контент должен быть объектом');
  }

  if (!Array.isArray(raw.dailyPool)) {
    throw new Error('Контент: ожидается массив dailyPool');
  }

  raw.dailyPool.forEach((item, i) => {
    if (!isContentPuzzle(item)) {
      throw new Error(
        `Контент: dailyPool[${i}] — у пазла должны быть строковые поля id, imageKey, full, title (опционально thumb)`
      );
    }
  });

  if (!Array.isArray(raw.packs)) {
    throw new Error('Контент: ожидается массив packs');
  }

  raw.packs.forEach((pack, pi) => {
    if (!isContentPack(pack)) {
      throw new Error(
        `Контент: packs[${pi}] — у пака должны быть id, title и непустой массив puzzles с полями id, imageKey, full, title`
      );
    }
  });
}

/**
 * Загружает content.json, валидирует и кэширует. Повторный вызов возвращает тот же результат.
 */
export async function loadContent(): Promise<ContentJson> {
  if (cached !== null) {
    return cached;
  }

  const res = await fetch(getContentUrl());
  if (!res.ok) {
    throw new Error(`Не удалось загрузить контент: ${res.status} ${res.statusText}`);
  }

  const raw: unknown = await res.json();
  validateContent(raw);
  cached = raw;
  console.log(
    'Content loaded:',
    { packs: cached.packs.length, dailyPool: cached.dailyPool.length },
    'first pack titles:',
    cached.packs[0]?.puzzles.map((p) => p.title) ?? []
  );
  return cached;
}

/**
 * Возвращает закэшированный контент. Вызывать после loadContent(), иначе выбросит.
 */
export function getContent(): ContentJson {
  if (cached === null) {
    throw new Error('Content not loaded. Call ContentLoader.loadContent() first.');
  }
  return cached;
}
